// SAM-199 — service picker for the front-desk "book a SERVICE" path.
//
// New component: Tailwind + tokens.css only, no baseui/styletron (SAM-175 freeze).
// Mirrors ProductSelectControl's props shape (businessId/defaultValue/onChange) so
// AccessForm can swap between the two without a different integration pattern, but
// only lists services that are actually bookable — see filterBookableServices in
// serviceBooking.ts ("active" + synced to the booking engine) — plus the active pass_fee
// services, which are sold with a payment link instead (SAM-1171, filterOfferableServices).

import { useMemo } from 'react';
import { CalendarClock, Loader2 } from 'lucide-react';
import { useServices, type Service } from '../../../hooks/useServices';
import { filterOfferableServices } from './serviceBooking';
import { useT } from '../../../i18n/admin';

type Props = {
  businessId: string | null;
  value: Service | null;
  onChange: (service: Service | null) => void;
  disabled?: boolean;
};

export const ServiceSelectControl = ({ businessId, value, onChange, disabled = false }: Props) => {
  const { data: services, isLoading, error } = useServices(businessId);
  const { t, lang } = useT();

  const bookableServices = useMemo(() => filterOfferableServices(services), [services]);

  const placeholder = !businessId
    ? t('booking.businessIdRequired')
    : isLoading
    ? t('booking.service.loadingShort')
    : error
    ? t('booking.service.loadError')
    : bookableServices.length === 0
    ? t('booking.service.noneBookable')
    : t('booking.service.select');

  return (
    <div className="fc">
      <div className="relative">
        <select
          value={value?.id || ''}
          disabled={disabled || isLoading || !!error || !businessId || bookableServices.length === 0}
          onChange={(e) => {
            const selected = bookableServices.find((s) => s.id === e.target.value) || null;
            onChange(selected);
          }}
        >
          <option value="" disabled>{placeholder}</option>
          {bookableServices.map((service) => (
            <option key={service.id} value={service.id}>
              {lang === 'es'
                ? service.name_es || service.name || service.name_en
                : service.name_en || service.name || service.name_es}
            </option>
          ))}
        </select>
      </div>
      {isLoading && (
        <div className="cap" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Loader2 size={12} className="animate-spin" /> {t('booking.service.loading')}
        </div>
      )}
      {!isLoading && !error && businessId && bookableServices.length === 0 && (
        <div className="cap" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <CalendarClock size={12} />
          {t('booking.service.noneSynced')}
        </div>
      )}
    </div>
  );
};
